import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  fetchAllPostsAdmin,
  createPostAdmin,
  updatePostAdmin,
  deletePostAdmin,
} from '../../redux/slices/Blog/blogSlice';
import { getAuthHeader } from '../../services/api';
import axios from 'axios';

const AdminBlogPosts = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const blogState = useSelector((state) => state.blog) || {};
  const { adminPosts = [], isLoading } = blogState;
  

  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    titleEn: '',
    titleEs: '',
    contentEn: '',
    contentEs: '',
    slug: '',
    image: '',
    tags: '',
    isPublished: true,
  });
  const [langTab, setLangTab] = useState('en');
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    dispatch(fetchAllPostsAdmin());
  }, [dispatch]);

  const resetForm = () => {
    setFormData({
      titleEn: '', titleEs: '', contentEn: '', contentEs: '',
      slug: '', image: '', tags: '', isPublished: true,
    });
    setEditingPost(null);
    setImageFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSlugGenerate = () => {
    const base = formData.titleEn || formData.titleEs || 'post';
    const slug = base.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    setFormData({ ...formData, slug });
  };

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()),
      author: null, // will be set on backend
    };
    if (editingPost) {
      await dispatch(updatePostAdmin({ id: editingPost._id, data }));
    } else {
      await dispatch(createPostAdmin(data));
    }
    resetForm();
    dispatch(fetchAllPostsAdmin());
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      titleEn: post.titleEn,
      titleEs: post.titleEs,
      contentEn: post.contentEn,
      contentEs: post.contentEs,
      slug: post.slug,
      image: post.image || '',
      tags: post.tags?.join(', ') || '',
      isPublished: post.isPublished,
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this post?')) {
      await dispatch(deletePostAdmin(id));
      dispatch(fetchAllPostsAdmin());
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Form */}
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h2 className="text-xl font-semibold mb-4">
          {editingPost ? 'Edit Post' : 'Create New Post'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Language Tabs */}
          <div className="flex border-b">
            <button
              type="button"
              className={`px-4 py-2 ${langTab === 'en' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
              onClick={() => setLangTab('en')}
            >
              English
            </button>
            <button
              type="button"
              className={`px-4 py-2 ${langTab === 'es' ? 'border-b-2 border-red-600 text-red-600' : 'text-gray-500'}`}
              onClick={() => setLangTab('es')}
            >
              Español
            </button>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {langTab === 'en' ? 'Title (English)' : 'Título (Español)'}
            </label>
            <input
              type="text"
              name={langTab === 'en' ? 'titleEn' : 'titleEs'}
              value={langTab === 'en' ? formData.titleEn : formData.titleEs}
              onChange={handleInputChange}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {langTab === 'en' ? 'Content (English)' : 'Contenido (Español)'}
            </label>
            <textarea
              name={langTab === 'en' ? 'contentEn' : 'contentEs'}
              value={langTab === 'en' ? formData.contentEn : formData.contentEs}
              onChange={handleInputChange}
              rows="6"
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          {/* Slug (auto-generate) */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Slug</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>
            <button
              type="button"
              onClick={handleSlugGenerate}
              className="self-end px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Generate
            </button>
          </div>

          {/* Image Upload */}
              <div>
      <label className="block text-sm font-medium text-gray-700">Image URL (Cloudinary)</label>
                <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="Paste your Cloudinary image URL here"
                    className="w-full border rounded-lg px-3 py-2"
                />
                {formData.image && (
                    <img src={formData.image} alt="Preview" className="h-32 object-cover rounded mt-2" />
                )}
                </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Tags (comma separated)</label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="e.g., tips, health, news"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          {/* Published */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isPublished"
              checked={formData.isPublished}
              onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
              className="h-4 w-4 text-red-600"
            />
            <label className="ml-2 text-sm text-gray-700">Published</label>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
            >
              {editingPost ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-200 hover:bg-gray-300 px-6 py-2 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Post List */}
      <div className="bg-white p-6 rounded-xl shadow-md">
        <h2 className="text-xl font-semibold mb-4">All Posts</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead>
              <tr>
                <th className="border p-2 text-left">Title (EN)</th>
                <th className="border p-2 text-left">Slug</th>
                <th className="border p-2 text-left">Status</th>
                <th className="border p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
  {adminPosts.length > 0 ? (
    adminPosts.map((post) => (
      <tr key={post._id}>
        <td className="border p-2">{post.titleEn}</td>
        <td className="border p-2">{post.slug}</td>
        <td className="border p-2">{post.isPublished ? 'Published' : 'Draft'}</td>
        <td className="border p-2">
          <button onClick={() => handleEdit(post)} className="bg-blue-500 text-white px-2 py-1 rounded mr-2">
            Edit
          </button>
          <button onClick={() => handleDelete(post._id)} className="bg-red-500 text-white px-2 py-1 rounded">
            Delete
          </button>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="4" className="text-center py-4 text-gray-500">
        No posts found.
      </td>
    </tr>
  )}
</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogPosts;